import { useCallback, useState } from "react";
import { buildRoute } from "fetch-run";
import { ExtractRouteParams, WithOptionalRouteParams } from "./types";

type Operation<Req extends object | void, Res extends object | void> = [
  (data: Req) => Promise<Res>,
  boolean,
  Error | null,
];

function useOperationBase<Req extends object | void, Res extends object | void>(
  execute: (data: Req, url: string | null) => Promise<Res>,
  urlResolver?: () => string,
) {
  // State
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setLoading] = useState(false);

  // Methods
  const mutate = useCallback(
    async (data: Req): Promise<Res> => {
      setError(null);
      setLoading(true);

      const url = urlResolver?.() ?? null;

      try {
        const res = await execute(data, url);
        return res;
      } catch (err) {
        if (err instanceof Error) {
          setError(err);
        }
        // Always throw
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [urlResolver, execute],
  );

  // Public
  return [mutate, isLoading, error] as const;
}

export function createOperation<
  Req extends object | void,
  Res extends object | void,
>(execute: (data: Req) => Promise<Res>) {
  const useOperation = () => {
    return useOperationBase(execute);
  };

  return useOperation;
}

export function createRouteOperation<
  R extends string,
  Req extends object | void,
  Res extends object | void,
>(route: R, execute: (url: string, data: Req) => Promise<Res>) {
  type _RouteParams = ExtractRouteParams<R>;

  const useOperation = (routeParams: _RouteParams = {} as _RouteParams) => {
    return useOperationBase(
      (data: Req, url: string | null) => {
        // `url` is always defined here because of the `urlResolver` below
        return execute(url!, data);
      },
      () => {
        return buildRoute(route, routeParams ?? {});
      },
    );
  };

  type UseOperationFn = WithOptionalRouteParams<
    _RouteParams,
    Operation<Req, Res>
  >;

  return useOperation as UseOperationFn;
}
